import React, { useState, useEffect } from "react";
import {
  FileText,
  Download,
  PlusCircle,
  AlertCircle,
  Pencil,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import MenuBar from "@/components/MenuBar";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { format } from "date-fns";
import { AgreementDownloadUrlResponse } from "@/types/employment-agreement";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Interface matching backend POJO (or expected structure)
interface Agreement {
  id: number;
  employer_id: number;
  employee_id: number;
  pdf_path: string; // This is now the S3 key
  agreement_data: {
    employerName: string;
    employeeName: string;
    startDate?: string | null; // Allow null or undefined
    salaryAmount?: number | null;
  };
  created_at: string;
  status: string;
}

// Define expected success response structure
interface AgreementsSuccessResponse {
  agreements: Agreement[];
  status: "success";
}

// Define possible error response structure (adjust if known)
interface AgreementsErrorResponse {
  status: string; // e.g., "error"
  error?: string; // Optional error message from backend
  // Add other potential error fields if applicable
}

type AgreementsResponse = AgreementsSuccessResponse | AgreementsErrorResponse;

// Type guard to check for success response
function isSuccessResponse(
  response: AgreementsResponse,
): response is AgreementsSuccessResponse {
  return (
    response.status === "success" &&
    Array.isArray((response as AgreementsSuccessResponse).agreements)
  );
}

// Delete response interface
interface DeleteAgreementResponse {
  status: string;
  message?: string;
  error?: string;
}

export default function EmployerContractsView() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agreementToDelete, setAgreementToDelete] = useState<number | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    setIsLoading(true);
    setFetchError(null);
    setAgreements([]); // Reset agreements on new fetch
    try {
      if (!user) {
        throw new Error("Not authorized");
      }

      // Make the API request
      const response = await ApiService.request<AgreementsResponse>(
        EndpointKeys.LIST_EMPLOYER_AGREEMENTS,
        { method: "GET" },
      );

      // Use the type guard to check for successful status and valid data structure
      if (isSuccessResponse(response)) {
        setAgreements(response.agreements);
      } else {
        // Handle cases where the request succeeded (2xx) but the backend indicates an error
        const backendError =
          response.error || "Received unexpected data format from server.";
        console.error(
          "Backend indicated error:",
          backendError,
          "Response:",
          response,
        );
        throw new Error(backendError);
      }
    } catch (error) {
      // This catches network errors, non-2xx responses from ApiService, and errors thrown above
      console.error("Error fetching employer agreements:", error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : "Failed to load employment contracts";
      setFetchError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadAgreement = async (agreementId: number) => {
    try {
      // Get the presigned URL for the specific agreement
      const response = await ApiService.request<AgreementDownloadUrlResponse>(
        EndpointKeys.GET_AGREEMENT_DOWNLOAD_URL,
        {
          method: "GET",
          queryParams: { agreement_id: agreementId },
        },
      );

      if (response.presignedUrl) {
        // Open the document in a new tab
        window.open(response.presignedUrl, "_blank");
      } else {
        throw new Error("Failed to get download URL for the agreement");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to download agreement",
        variant: "destructive",
      });
    }
  };

  const handleEditClick = (agreementId: number) => {
    navigate(`/contract/edit/${agreementId}`);
  };

  const handleDeleteClick = (agreementId: number) => {
    setAgreementToDelete(agreementId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!agreementToDelete) return;

    setIsDeleting(true);
    try {
      const response = await ApiService.request<DeleteAgreementResponse>(
        EndpointKeys.DELETE_AGREEMENT,
        {
          method: "DELETE",
          urlParams: {
            agreement_id: agreementToDelete,
          },
        },
      );

      if (response.status === "success") {
        // Remove the deleted agreement from the state
        setAgreements(agreements.filter((a) => a.id !== agreementToDelete));
        toast({
          title: "Success",
          description: "Contract deleted successfully",
        });
      } else {
        throw new Error(response.error || "Failed to delete contract");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to delete contract",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setAgreementToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setAgreementToDelete(null);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Not specified";
    try {
      const date = new Date(dateString);
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date value");
      }
      return format(date, "dd/MM/yyyy");
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return "Invalid Date";
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-1/4" />
          </CardContent>
        </Card>
      );
    }

    if (fetchError) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Contracts</AlertTitle>
          <AlertDescription>
            {fetchError}. Please try refreshing the page or contact support if
            the issue persists.
          </AlertDescription>
        </Alert>
      );
    }

    if (agreements.length === 0) {
      return (
        <Card>
          <CardHeader>
            <CardTitle>No Contracts Available</CardTitle>
            <CardDescription>
              You haven't generated any employment contracts yet. Click the
              'Generate New Contract' button above to create one.
            </CardDescription>
          </CardHeader>
        </Card>
      );
    }

    return agreements.map((agreement) => (
      <Card key={agreement.id} className="mb-4">
        <CardHeader>
          <CardTitle>
            Contract for {agreement.agreement_data?.employeeName || "N/A"} -
            Generated {formatDate(agreement.created_at)}
          </CardTitle>
          <CardDescription>
            Status:{" "}
            {agreement.status.charAt(0).toUpperCase() +
              agreement.status.slice(1)}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium">Employee</p>
              <p className="text-sm text-muted-foreground">
                {agreement.agreement_data?.employeeName || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium">Start Date</p>
              <p className="text-sm text-muted-foreground">
                {formatDate(agreement.agreement_data?.startDate)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium">Position</p>
              <p className="text-sm text-muted-foreground">Nanny (Assumed)</p>
            </div>
            <div>
              <p className="text-sm font-medium">Salary</p>
              <p className="text-sm text-muted-foreground">
                £
                {agreement.agreement_data?.salaryAmount?.toLocaleString() ||
                  "N/A"}{" "}
                per year
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => downloadAgreement(agreement.id)}
              className="mt-4"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Contract
            </Button>
            <Button
              variant="outline"
              onClick={() => handleEditClick(agreement.id)}
              className="mt-4"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit Contract
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteClick(agreement.id)}
              className="mt-4"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Contract
            </Button>
          </div>
        </CardContent>
      </Card>
    ));
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the contract. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete} disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile Menu Toggle - Consider making this a reusable component */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <FileText className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      <div className="flex-1 w-full md:w-auto">
        <div className="p-4 md:p-6 max-w-7xl space-y-6 mt-16 md:mt-0">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl">Employment Contracts</h2>
            <Button onClick={() => navigate("/contract/generate")}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Generate New Contract
            </Button>
          </div>

          <Alert className="mb-4">
            <FileText className="h-4 w-4 text-primary" />
            <AlertTitle>Your Employment Contracts</AlertTitle>
            <AlertDescription>
              Here you can view, download, edit, or delete the employment
              contracts you have generated for your employees. Click 'Generate
              New Contract' to create a new agreement.
            </AlertDescription>
          </Alert>

          {renderContent()}
        </div>
      </div>
    </div>
  );
}
